import { AgGridCellTooltipComponent, AgGridCellTooltipValueGetter } from 'components/AgGrid/AgGridCellTooltipComponent';
import { numberFormatter } from 'components/AgGrid/utils';
import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import type { ContragentTypeEnum, MtrTypeEnum } from 'gql/graphql';
import { SortEnumType, WorkOrderStatusEnum } from 'gql/graphql';

import type { TextFieldPropValue } from '@consta/uikit/TextField';
import { useMemo } from 'react';

import { labelContragentType, labelMtrType } from '../../../MatchMtrDialog/hooks/useUerMtrRowsForComparisonColumns';
import { gqlWorkOrderMtrPriceList } from '../../workOrderQueries';
import type { AddMtrVariables } from '../AddMtrDialog';
import { CellRenderTextField } from '../cellRender/CellRenderTextField';

interface IColumnsAddMtr {
    variables: AddMtrVariables;
    changeFilterHandler: (params: { fieldName: string; checkedValues: string[] }) => void;
    workOrderStatus?: WorkOrderStatusEnum;
    checkedIds: Record<string, boolean>;
    valueNote: Record<string, string>;
    handleChangesValueNote: (rowId: number) => ({ value }: { value: TextFieldPropValue }) => void;
}

export const useColumnsAddMtr = ({
    variables,
    changeFilterHandler,
    workOrderStatus,
    checkedIds,
    valueNote,
    handleChangesValueNote,
}: IColumnsAddMtr) => {
    const columns = useMemo(
        () => [
            {
                headerName: 'Шифр',
                field: 'code',
                width: 160,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    dataAccessor: 'uerWorkOrderPriceListMtr',
                    query: gqlWorkOrderMtrPriceList,
                    variables: {
                        ...variables,
                    },
                    whereVariables: ['code'],
                    childFieldAccessor: 'code',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'code',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Наименование',
                spanHeaderHeight: true,
                suppressMovable: true,
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
                field: 'name',
                width: 320,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    dataAccessor: 'uerWorkOrderPriceListMtr',
                    query: gqlWorkOrderMtrPriceList,
                    variables: {
                        ...variables,
                        order: [{ name: SortEnumType.Asc }],
                    },
                    whereVariables: ['name'],
                    childFieldAccessor: 'name',
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'name',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Ед. изм',
                field: 'unit',
                width: 88,
            },
            {
                headerName: 'Стоимость единицы, руб. без ндс',
                field: 'unitCost',
                cellStyle: { textAlign: 'right' },
                width: 200,
                valueFormatter: numberFormatter(0),
            },
            {
                headerName: 'Поставка',
                field: 'supplyBy',
                width: 150,
                valueFormatter: ({ data }: { data?: { supplyBy?: ContragentTypeEnum } }) =>
                    data?.supplyBy ? labelContragentType[data?.supplyBy] : '',
            },
            {
                headerName: 'Тип',
                field: 'mtrType',
                flex: 1,
                valueFormatter: ({ data }: { data?: { mtrType?: MtrTypeEnum } }) =>
                    data?.mtrType ? labelMtrType[data.mtrType] : '',
            },
            ...(workOrderStatus === WorkOrderStatusEnum.InProgress
                ? [
                      {
                          headerName: 'Примечание',
                          field: 'note',
                          width: 200,
                          cellRenderer: CellRenderTextField,
                          cellRendererParams: {
                              checkedIds,
                              valueNote,
                              handleChangesValueNote,
                          },
                      },
                  ]
                : []),
        ],
        [changeFilterHandler, checkedIds, handleChangesValueNote, valueNote, variables, workOrderStatus],
    );

    return {
        columns,
    };
};
